import { useRef, useState, useLayoutEffect } from 'react';

/**
 * Hook para detectar qué elementos están en la última fila de un
 * contenedor con elementos que pueden renderizarse en múltiples filas.
 *
 * @param {Array} dependencies - Array de dependencias que deberían desencadenar un recálculo
 * @returns {Object} - Objeto con la referencia del contenedor, los índices de la última fila y una función para recalcular
 */
const useLastRowDetection = (dependencies = []) => {
  const containerRef = useRef(null);
  const [lastRowIndexes, setLastRowIndexes] = useState([]);

  /**
   * Calcula los índices de los elementos que están en la última fila del contenedor.
   */
  const checkLastRow = () => {
    if (!containerRef.current) return;

    const children = Array.from(containerRef.current.children);
    const lastRowIndexes = [];
    let previousTop = null;

    children.forEach((element, index) => {
      const elementBox = element.getBoundingClientRect();

      // Si cambia la posición vertical, estamos en una nueva fila
      if (previousTop !== null && elementBox.top !== previousTop) {
        lastRowIndexes.length = 0; // Limpia los índices anteriores
      }

      // Añade el índice actual a la última fila conocida
      lastRowIndexes.push(index);

      // Actualiza la referencia de posición vertical
      previousTop = elementBox.top;
    });

    setLastRowIndexes(lastRowIndexes);
  };

  // Ejecuta el cálculo cuando cambien las dependencias:
  useLayoutEffect(() => {
    checkLastRow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  // También se recalcula al cambiar el tamaño de la ventana:
  useLayoutEffect(() => {
    window.addEventListener('resize', checkLastRow);
    return () => window.removeEventListener('resize', checkLastRow);
  }, []);

  return {
    containerRef,
    lastRowIndexes,
    recalculate: checkLastRow
  };
};

export default useLastRowDetection;
